// Firebase Error Interfaces
export interface FirebaseError {
  code: string;
  message: string;
}

export interface FirebaseAuthError extends FirebaseError {
  code: 
    | 'auth/invalid-credential'
    | 'auth/user-not-found' 
    | 'auth/wrong-password'
    | 'auth/invalid-email'
    | 'auth/user-disabled'
    | 'auth/too-many-requests'
    | 'auth/email-already-in-use'
    | 'auth/weak-password'
    | 'auth/operation-not-allowed'
    | string; // Allow other auth error codes
}

export interface FirebaseStorageError extends FirebaseError {
  code: 
    | 'storage/object-not-found'
    | 'storage/unauthorized'
    | 'storage/canceled'
    | 'storage/unknown'
    | string; // Allow other storage error codes
}

export interface User {
  id: string;
  email: string;
  name?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Subscription and Access Management Types
export interface SubscriptionInfo {
  id: string;
  userId: string;
  status: 'active' | 'expired' | 'cancelled' | 'pending';
  startDate: Date;
  endDate: Date;
  autoRenew: boolean;
  paymentMethod?: string;
  lastPaymentDate?: Date;
  nextBillingDate?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface UserProfile {
  id: string;
  uid: string; // Firebase Auth UID
  email: string;
  displayName?: string;
  subscription: SubscriptionInfo;
  createdAt: Date;
  updatedAt: Date;
}

export interface AccessState {
  hasAccess: boolean;
  daysRemaining: number;
  isExpired: boolean;
  isGracePeriod: boolean;
  gracePeriodDays?: number;
  expirationDate: Date;
  canExtend: boolean;
  subscriptionStatus: 'active' | 'expired' | 'cancelled' | 'pending';
}

export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

export interface Restaurant {
  id: string;
  ownerId: string;
  name: string;
  description?: string;
  phone?: string;
  address?: string;
  slug: string;
  logoUrl?: string;
  coverImageUrl?: string;
  themeColor?: string;  // Single theme color field
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface RestaurantSettings {
  allowSearch: boolean;
  defaultSectionsCollapsed: boolean;
  showDietIcons: boolean;
  showPrices: boolean;
  enableOrdering: boolean;
  customCSS?: string;
}

export type DietType = 'veg' | 'non-veg' | 'contains-eggs';

export interface DietTypeInfo {
  key: DietType;
  label: string;
  description: string;
  icon: string;
  color: string;
  textColor: string;
}

export interface MenuSection {
  id: string;
  restaurantId: string;
  name: string;
  description?: string;
  order: number;
  isVisible: boolean;
  collapsed: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ImageMetadata {
  fileName: string;
  size: number;
  type: string;
  dimensions: { width: number; height: number };
  uploadedAt: Date;
  url: string;
}

export interface MenuItem {
  id: string;
  restaurantId: string;
  sectionId: string;
  name: string;
  description?: string;
  price: number;
  originalPrice?: number; // For showing discounts
  dietType: DietType;
  imageUrl?: string;
  imageMetadata?: ImageMetadata;
  ingredients?: string[];
  allergens?: string[];
  order: number;
  isAvailable: boolean;
  isPopular: boolean;
  isNew: boolean;
  preparationTime?: number; // in minutes
  calories?: number;
  spiceLevel?: 1 | 2 | 3 | 4 | 5; // 1 = mild, 5 = very spicy
  isVegan?: boolean;
  isJain?: boolean;
  // Enhanced diet type fields
  enhancedAllergens?: AllergenInfo;
  enhancedSpiceLevel?: SpiceLevel;
  cuisineTags?: CuisineTag[];
  customTags?: CustomDietaryTag[];
  isSignatureDish?: boolean;
  isChefSpecial?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Menu {
  restaurant: Restaurant;
  sections: MenuSection[];
  items: MenuItem[];
  settings: RestaurantSettings;
}

// QR Code Management Types
export interface QRCodeConfig {
  restaurantId: string;
  url: string; // Public menu URL: /menu/{restaurantId}
  size: 'small' | 'medium' | 'large' | 'extra-large';
  foregroundColor: string; // Default: black
  backgroundColor: string; // Default: white
  includeRestaurantLogo: boolean;
}

export interface QRCodeDownloadOptions {
  format: 'png' | 'svg' | 'pdf';
  quality: 'high'; // Always high quality for print
  size: { width: number; height: number }; // in pixels
}

export interface RestaurantQRCode {
  id: string;
  restaurantId: string;
  config: QRCodeConfig;
  createdAt: Date;
  lastUpdated: Date;
  downloadCount: number;
  analytics: {
    scansTotal: number;
    scansThisMonth: number;
    lastScanned?: Date;
  };
}

// Analytics enhancement for QR tracking
export interface QRCodeScanEvent {
  restaurantId: string;
  source: 'qr-code' | 'direct-url';
  timestamp: Date;
  userAgent?: string;
  referrer?: string;
}

// QR Code generation state
export interface QRCodeState {
  config: QRCodeConfig;
  preview: string | null; // Base64 data URL
  generating: boolean;
  downloading: boolean;
  error: string | null;
  qrCode: RestaurantQRCode | null;
}

export interface LoginFormData {
  email: string;
  password: string;
}

export interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
  name?: string;
}

export interface RestaurantFormData {
  name: string;
  description?: string;
  phone?: string;
  address?: string;
  themeColor?: string;
}

export interface MenuItemFormData {
  name: string;
  description?: string;
  price: string; // As string for form handling
  dietType: DietType;
  sectionId: string;
  ingredients?: string;
  allergens?: string;
  spiceLevel?: 1 | 2 | 3 | 4 | 5;
  preparationTime?: string; // As string for form handling
  calories?: string; // As string for form handling
  isVegan?: boolean;
  isJain?: boolean;
  // Enhanced diet type fields
  enhancedAllergens?: AllergenInfo;
  enhancedSpiceLevel?: SpiceLevel;
  cuisineTags?: CuisineTag[];
  customTags?: CustomDietaryTag[];
  isSignatureDish?: boolean;
  isChefSpecial?: boolean;
}

export interface MenuSectionFormData {
  name: string;
  description?: string;
}

export interface AccountManagementFormData {
  displayName?: string;
  email?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface LoadingState {
  isLoading: boolean;
  message?: string;
  code?: string;
}

export interface ModalState {
  isOpen: boolean;
  type?: 'info' | 'warning' | 'error' | 'success';
  title?: string;
  message?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

export interface AccountModalState {
  isOpen: boolean;
  canClose: boolean; // false when access is expired
  activeTab: 'profile' | 'subscription' | 'extend';
  showExtendForm: boolean;
}

export interface SearchFilters {
  query?: string;
  dietTypes?: DietType[];
  priceRange?: {
    min: number;
    max: number;
  };
  spiceLevel?: number[];
  isAvailable?: boolean;
  isPopular?: boolean;
  isNew?: boolean;
}

export interface SearchResult {
  items: MenuItem[];
  totalResults: number;
  filters: SearchFilters;
}

export interface ImageUploadState {
  uploading: boolean;
  progress: number;
  error: string | null;
  previewUrl: string | null;
}

export interface ImageFile {
  id: string;
  file: File;
  previewUrl: string;
  uploadState: ImageUploadState;
}

export interface OptimizedImage {
  file: File;
  originalSize: number;
  compressedSize: number;
  dimensions: { width: number; height: number };
}

export interface UploadConfig {
  maxFileSize: number; // 5MB
  maxDimensions: { width: number; height: number }; // 800x600
  allowedTypes: string[]; // ['image/jpeg', 'image/png', 'image/webp']
  compressionQuality: number; // 0.8
}

export interface UploadProgress {
  progress: number;
  status: 'idle' | 'uploading' | 'success' | 'error';
  error?: string;
}

export interface ImageUpload {
  file: File;
  preview: string;
  progress: UploadProgress;
}

export interface NavItem {
  label: string;
  path: string;
  icon?: string;
  requiresAuth?: boolean;
  children?: NavItem[];
}

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

// Image Menu Types
export interface ImageMenuItem {
  id: string;
  restaurantId: string;
  imageUrl: string;
  thumbnailUrl?: string; // Compressed thumbnail for grid view
  fileName: string;
  fileSize: number;
  dimensions: { width: number; height: number };
  order: number;
  uploadedAt: Date;
  updatedAt: Date;
}

export interface ImageMenuState {
  images: ImageMenuItem[];
  loading: boolean;
  uploading: boolean;
  uploadProgress: number;
  error: string | null;
  maxImages: number; // 50
  currentImageCount: number;
}

// Image menu display configuration
export interface ImageMenuDisplayConfig {
  showThumbnailRow: boolean;
  thumbnailCount: { mobile: number; desktop: number }; // { mobile: 3, desktop: 5 }
  showGridDirectly: boolean;
  hasDigitalMenu: boolean;
  hasAvailableDigitalItems: boolean;
}

// Customer preview state
export interface CustomerPreviewState {
  imageMenu: ImageMenuItem[];
  digitalMenu: {
    sections: MenuSection[];
    hasAvailableItems: boolean;
  };
  displayConfig: ImageMenuDisplayConfig;
  expandedImageIndex: number | null;
  isImageGridExpanded: boolean;
}

// Image menu upload state
export interface ImageMenuUploadState {
  files: File[];
  previews: string[];
  uploading: boolean;
  progress: Record<string, number>; // fileId -> progress percentage
  errors: Record<string, string>; // fileId -> error message
}

export interface MenuAnalytics {
  totalViews: number;
  uniqueVisitors: number;
  popularItems: {
    itemId: string;
    name: string;
    views: number;
  }[];
  searchQueries: {
    query: string;
    count: number;
  }[];
  deviceTypes: {
    mobile: number;
    tablet: number;
    desktop: number;
  };
  timeRange: {
    start: Date;
    end: Date;
  };
}

export type Partial<T> = {
  [P in keyof T]?: T[P];
};

export type Required<T> = {
  [P in keyof T]-?: T[P];
};

export type Pick<T, K extends keyof T> = {
  [P in K]: T[P];
};

export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export interface BaseComponentProps {
  className?: string;
  children?: React.ReactNode;
}

export interface ButtonProps extends BaseComponentProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
}

export interface InputProps extends BaseComponentProps {
  type?: string;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  disabled?: boolean;
  error?: string;
  label?: string;
  required?: boolean;
  onChange?: (value: string) => void;
}

export interface CardProps extends BaseComponentProps {
  variant?: 'default' | 'outlined' | 'elevated';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
}

export interface AuthStore {
  user: User | null;
  userProfile: UserProfile | null;
  accessState: AccessState | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  clearError: () => void;
  fetchUserProfile: () => Promise<void>;
  updateUserProfile: (data: Partial<UserProfile>) => Promise<void>;
  checkAccessState: () => Promise<AccessState>;
}

export interface RestaurantStore {
  currentRestaurant: Restaurant | null;
  restaurants: Restaurant[];
  loading: boolean;
  error: string | null;
  createRestaurant: (data: RestaurantFormData) => Promise<Restaurant>;
  updateRestaurant: (id: string, data: Partial<RestaurantFormData>) => Promise<void>;
  deleteRestaurant: (id: string) => Promise<void>;
  setCurrentRestaurant: (restaurant: Restaurant | null) => void;
  fetchRestaurants: () => Promise<void>;
}

export interface MenuStore {
  menu: Menu | null;
  loading: boolean;
  error: string | null;
  fetchMenu: (restaurantId: string) => Promise<void>;
  addSection: (data: MenuSectionFormData) => Promise<void>;
  updateSection: (id: string, data: Partial<MenuSectionFormData>) => Promise<void>;
  deleteSection: (id: string) => Promise<void>;
  addItem: (data: MenuItemFormData) => Promise<void>;
  updateItem: (id: string, data: Partial<MenuItemFormData>) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  reorderSections: (sections: MenuSection[]) => Promise<void>;
  reorderItems: (sectionId: string, items: MenuItem[]) => Promise<void>;
}

export const DEFAULT_THEME_COLOR = '#00809D'; // Default theme color

export const DIET_TYPES: Record<DietType, DietTypeInfo> = {
  veg: {
    key: 'veg',
    label: 'Vegetarian',
    description: 'Contains no meat, fish, or poultry',
    icon: 'veg',
    color: '#34C759',
    textColor: '#FFFFFF',
  },
  'non-veg': {
    key: 'non-veg',
    label: 'Non-Vegetarian',
    description: 'Contains meat, fish, or poultry',
    icon: 'non-veg',
    color: '#FF3B30',
    textColor: '#FFFFFF',
  },
  'contains-eggs': {
    key: 'contains-eggs',
    label: 'Contains Eggs',
    description: 'Contains eggs but no meat',
    icon: 'eggs',
    color: '#FF9500',
    textColor: '#FFFFFF',
  },
};

// Enhanced Diet Type System
export interface AllergenInfo {
  nuts: boolean;
  dairy: boolean;
  gluten: boolean;
  soy: boolean;
  eggs: boolean;
  shellfish: boolean;
  sesame: boolean;
  mustard: boolean;
  custom: string[];
  mayContain: string[]; // Cross-contamination warnings
}

export interface SpiceLevel {
  level: 'none' | 'mild' | 'medium' | 'hot' | 'extra-hot';
  preparationNotes?: string;
}

export interface CuisineTag {
  type: 'north-indian' | 'south-indian' | 'chinese' | 'continental' | 'street-food' | 'desserts' | 'beverages';
  region?: string; // e.g., 'punjabi', 'bengali', 'tamil'
  subType?: string; // e.g., 'indo-chinese', 'american-chinese'
}

export interface CustomDietaryTag {
  id: string;
  name: string;
  description?: string;
  category: 'ingredient' | 'preparation' | 'nutritional' | 'custom';
  color?: string; // For visual theming
}

// Enhanced Diet Type Form Data
export interface EnhancedDietTypeFormData {
  dietType: DietType; // Primary diet type (required)
  allergens: AllergenInfo;
  spiceLevel: SpiceLevel;
  cuisineTags: CuisineTag[];
  customTags: CustomDietaryTag[];
  ingredients: string[];
  preparationTime?: number;
  isSignatureDish: boolean;
  isChefSpecial: boolean;
}

// Diet type display configuration
export interface DietTypeDisplayConfig {
  showAllergens: boolean;
  showSpiceLevel: boolean;
  showCuisineTags: boolean;
  showCustomTags: boolean;
  compactMode: boolean; // For mobile display
}

export const SPICE_LEVELS = {
  none: { label: 'No Spice', emoji: '', color: '#8E8E93' },
  mild: { label: 'Mild', emoji: '', color: '#34C759' },
  medium: { label: 'Medium', emoji: '', color: '#FF9500' },
  hot: { label: 'Hot', emoji: '', color: '#FF6B00' },
  'extra-hot': { label: 'Extra Hot', emoji: '', color: '#FF3B30' },
} as const;

export const ALLERGEN_TYPES = {
  nuts: { label: 'Nuts', icon: '', color: '#8B4513' },
  dairy: { label: 'Dairy', icon: '', color: '#4FC3F7' },
  gluten: { label: 'Gluten', icon: '', color: '#FDD835' },
  soy: { label: 'Soy', icon: '', color: '#8BC34A' },
  eggs: { label: 'Eggs', icon: '', color: '#FFEB3B' },
  shellfish: { label: 'Shellfish', icon: '', color: '#FF7043' },
  sesame: { label: 'Sesame', icon: '', color: '#A1887F' },
  mustard: { label: 'Mustard', icon: '', color: '#CDDC39' },
} as const;

export const CUISINE_TYPES = {
  'north-indian': { label: 'North Indian', color: '#FF5722', regions: ['punjabi', 'delhi', 'rajasthani', 'kashmiri'] },
  'south-indian': { label: 'South Indian', color: '#4CAF50', regions: ['tamil', 'kerala', 'andhra', 'karnataka'] },
  'chinese': { label: 'Chinese', color: '#F44336', subTypes: ['indo-chinese', 'cantonese', 'szechuan'] },
  'continental': { label: 'Continental', color: '#9C27B0', subTypes: ['italian', 'mexican', 'american'] },
  'street-food': { label: 'Street Food', color: '#FF9800', regions: ['mumbai', 'delhi', 'bengali'] },
  'desserts': { label: 'Desserts', color: '#E91E63', subTypes: ['indian-sweets', 'western-desserts'] },
  'beverages': { label: 'Beverages', color: '#2196F3', subTypes: ['hot', 'cold', 'fresh-juices'] },
} as const;

export const ROUTES = {
  HOME: '/',
  SIGNIN: '/auth/signin',
  SIGNUP: '/auth/signup',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESTAURANTS: '/restaurants',
  MENU_BUILDER: '/restaurants/:id/menu',
  IMAGE_MENU: '/restaurants/:id/image-menu',
  QR_CODE_MANAGEMENT: '/restaurants/:id/qr-codes',
  PUBLIC_MENU: '/menu/:restaurantId', // Public menu access route
  DASHBOARD: '/dashboard',
  RESTAURANT_SETUP: '/dashboard/restaurant/setup',
  MENU_PREVIEW: '/dashboard/menu/preview',
  QR_CODES: '/dashboard/qr-codes',
  ANALYTICS: '/dashboard/analytics',
  SETTINGS: '/dashboard/settings',
} as const;

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    SIGNUP: '/auth/signup',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/forgot-password',
  },
  RESTAURANTS: {
    LIST: '/restaurants',
    CREATE: '/restaurants',
    UPDATE: '/restaurants/:id',
    DELETE: '/restaurants/:id',
    GET_BY_SLUG: '/restaurants/slug/:slug',
  },
  MENU: {
    GET: '/restaurants/:restaurantId/menu',
    SECTIONS: '/restaurants/:restaurantId/sections',
    ITEMS: '/restaurants/:restaurantId/items',
  },
  UPLOAD: {
    IMAGE: '/upload/image',
  },
} as const;
